import React, { Component } from 'react';
import classNames from 'classnames';
import { flowRight } from 'lodash';
import memoizeOne from 'memoize-one';
import PropTypes from 'prop-types';
import { pluginEmoji, EMOJI_TYPE } from 'wix-rich-content-plugin-emoji';
import { pluginEmoji as pluginEmojiViewer } from 'wix-rich-content-plugin-emoji/viewer';
import { pluginGiphy, GIPHY_TYPE } from 'wix-rich-content-plugin-giphy';
import { pluginGiphy as pluginGiphyViewer } from 'wix-rich-content-plugin-giphy/viewer';
import { pluginImage, IMAGE_TYPE } from 'wix-rich-content-plugin-image';
import { pluginImage as pluginImageViewer } from 'wix-rich-content-plugin-image/viewer';
import { pluginLink, LINK_TYPE } from 'wix-rich-content-plugin-link';
import { pluginLink as pluginLinkViewer } from 'wix-rich-content-plugin-link/viewer';
import { pluginMentions, MENTION_TYPE } from 'wix-rich-content-plugin-mentions';
import { pluginMentions as pluginMentionsViewer } from 'wix-rich-content-plugin-mentions/viewer';
import {
  pluginTextColor,
  TEXT_COLOR_TYPE,
} from 'wix-rich-content-plugin-text-color';
import { pluginTextColor as pluginTextColorViewer } from 'wix-rich-content-plugin-text-color/viewer';
import { pluginVideo, VIDEO_TYPE } from 'wix-rich-content-plugin-video';
import { pluginVideo as pluginVideoViewer } from 'wix-rich-content-plugin-video/viewer';
import { TPAComponentsProvider } from 'wix-ui-tpa/cssVars';
import { WixCommentsApiProvider, WixComments } from '@wix/comments-ooi-client';
import { WixCommentsTranslationsProvider } from '@wix/comments-ooi-translations-provider';
import {
  formatDate,
  urijs,
  getTPASettingsIsRatingsEnabled,
  getTPASettingsIsMandatoryRatingsEnabled,
  resolveLegacyId,
} from '@wix/communities-blog-client-common';
import {
  EXPERIMENT_WIX_COMMENTS_USE_REACTIONS,
  EXPERIMENT_ENABLE_RATINGS_IN_VIEWER,
} from '@wix/communities-blog-experiments';
import { EditorSmallIcon } from '../../../common/components/icons/editor-small-icon';
import { OwnerSmallIcon } from '../../../common/components/icons/owner-small-icon';
import { WriterIcon } from '../../../common/components/icons/writer-icon';
import { RootPropsContext } from '../../../common/components/root-props/root-props-context';
import { connect } from '../../../common/components/runtime-context';
import withDeviceType from '../../../common/hoc/with-device-type';
import withExperiment from '../../../common/hoc/with-experiment';
import withFontClassName from '../../../common/hoc/with-font-class-name';
import withPostBorderWidth from '../../../common/hoc/with-post-border-width';
import { isRtlLanguage } from '../../../common/services/is-rtl-language';
import {
  getCommentId,
  getLanguage,
  getUrl,
  isSite,
} from '../../../common/store/basic-params/basic-params-selectors';
import styles from './post-page-wix-comments.scss';

class PostPageWixComments extends Component {
  getPluginConfig = memoizeOne((fetchMentions) => {
    return {
      [MENTION_TYPE]: {
        editorModule: pluginMentions,
        viewerModule: pluginMentionsViewer,
        config: {
          getMentions: fetchMentions,
          supportWhitespace: false,
        },
      },
      [LINK_TYPE]: { editorModule: pluginLink, viewerModule: pluginLinkViewer },
      [EMOJI_TYPE]: {
        editorModule: pluginEmoji,
        viewerModule: pluginEmojiViewer,
      },
      [IMAGE_TYPE]: {
        editorModule: pluginImage,
        viewerModule: pluginImageViewer,
      },
      [GIPHY_TYPE]: {
        editorModule: pluginGiphy,
        viewerModule: pluginGiphyViewer,
      },
      [VIDEO_TYPE]: {
        editorModule: pluginVideo,
        viewerModule: pluginVideoViewer,
      },
      [TEXT_COLOR_TYPE]: {
        editorModule: pluginTextColor,
        viewerModule: pluginTextColorViewer,
      },
    };
  });

  getTpaProviderProps = memoizeOne((isMobile, lng) => ({
    mobile: isMobile,
    rtl: isRtlLanguage(lng),
  }));

  getDeepLinkProp = memoizeOne((commentId, isSite) => ({
    commentId,
    generateLink: (commentId) => {
      if (isSite) {
        const url = urijs(window.location.href);
        url.setQuery({ commentId });

        return url.toString();
      }
    },
  }));

  getCtxFields = memoizeOne((postId) => ({
    claims: [],
    contextId: postId,
    contextType: 'postId',
  }));

  getReactionsConfig = memoizeOne((isWixCommentsUseReactionsEnabled) =>
    isWixCommentsUseReactionsEnabled
      ? {
          commentReactions: { type: 'emotions' },
          replyReactions: { type: 'emotions' },
        }
      : {},
  );

  getRatingsConfig = memoizeOne(
    ({ isRatingsFeatureEnabled, isRatingsEnabled, isRatingsMandatory }) =>
      isRatingsFeatureEnabled & isRatingsEnabled
        ? { isRequired: isRatingsMandatory }
        : undefined,
  );

  formatRelativeTime = (time) =>
    formatDate({ date: time, lng: this.props.language });

  render() {
    const {
      post,
      borderWidth,
      lng,
      isMobile,
      isSledRun,
      commentId,
      isSite,
      fetchMentions,
      isWixCommentsUseReactionsEnabled,
      isRatingsFeatureEnabled,
      isRatingsEnabled,
      isRatingsMandatory,
    } = this.props;

    return (
      <div
        className={classNames(
          styles.container,
          'blog-card-border-color',
          'blog-card-background-color',
        )}
        style={{ '--wcBorderWidth': `${borderWidth}px`, borderWidth }}
      >
        <div className={styles.innerContainer}>
          <RootPropsContext.Consumer>
            {(props) => {
              return (
                <WixCommentsApiProvider {...props}>
                  <WixCommentsTranslationsProvider>
                    <TPAComponentsProvider
                      value={this.getTpaProviderProps(isMobile, lng)}
                    >
                      <WixComments
                        hideZeroCommentsEmptyState
                        screenshotMode={isSledRun}
                        pluginConfig={this.getPluginConfig(fetchMentions)}
                        resourceId={resolveLegacyId(post)}
                        deepLink={this.getDeepLinkProp(commentId, isSite)}
                        ctxFields={this.getCtxFields(resolveLegacyId(post))}
                        pagination={PAGINATION_CONFIG}
                        formatRelativeTime={this.formatRelativeTime}
                        isLocked={post.isCommentsDisabled}
                        roleIcons={ROLE_ICONS}
                        reactions={this.getReactionsConfig(
                          isWixCommentsUseReactionsEnabled,
                        )}
                        a11yOptions={ALLY_OPTIONS}
                        contentTruncationLimit={isMobile ? 80 : 120}
                        ratings={this.getRatingsConfig({
                          isRatingsFeatureEnabled,
                          isRatingsEnabled,
                          isRatingsMandatory,
                        })}
                        useRicosNext
                      />
                    </TPAComponentsProvider>
                  </WixCommentsTranslationsProvider>
                </WixCommentsApiProvider>
              );
            }}
          </RootPropsContext.Consumer>
        </div>
      </div>
    );
  }
}

const ALLY_OPTIONS = { headerRank: 'h2' };

const ROLE_ICONS = {
  OWNER: OwnerSmallIcon,
  CONTRIBUTOR: OwnerSmallIcon,
  BLOG_WRITER: WriterIcon,
  BLOG_EDITOR: EditorSmallIcon,
};

const PAGINATION_CONFIG = {
  replyShowMoreLimit: 8,
  initialPage: {
    commentLimit: 20,
    replyLimit: 0,
  },
  pagination: {
    commentLimit: 20,
    replyLimit: 0,
  },
  maxPagesBeforeDrop: {
    commentPages: 2,
    replyPages: 4,
  },
};

PostPageWixComments.propTypes = {
  post: PropTypes.object,
  borderWidth: PropTypes.number.isRequired,
  contentFontClassName: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  isSledRun: PropTypes.bool,
  lng: PropTypes.string,
  fetchMentions: PropTypes.func.isRequired,
  commentId: PropTypes.string,
  getCommentDeepLink: PropTypes.func.isRequired,
  isWixCommentsUseReactionsEnabled: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  isRatingsFeatureEnabled: PropTypes.bool.isRequired,
  isRatingsEnabled: PropTypes.bool.isRequired,
  isRatingsMandatory: PropTypes.bool.isRequired,
};

const mapRuntimeToProps = (state, _, actions) => {
  return {
    isSledRun: getUrl(state).includes('viewerPlatformOverrides'),
    fetchMentions: actions.fetchMentionsPromisified,
    commentId: getCommentId(state),
    isSite: isSite(state),
    language: getLanguage(state),
    isRatingsEnabled: getTPASettingsIsRatingsEnabled(state),
    isRatingsMandatory: getTPASettingsIsMandatoryRatingsEnabled(state),
  };
};

export default flowRight(
  connect(mapRuntimeToProps),
  withDeviceType,
  withFontClassName,
  withPostBorderWidth,
  withExperiment({
    isWixCommentsUseReactionsEnabled: EXPERIMENT_WIX_COMMENTS_USE_REACTIONS,
    isRatingsFeatureEnabled: EXPERIMENT_ENABLE_RATINGS_IN_VIEWER,
  }),
)(PostPageWixComments);
